import type { AppTranslateFn } from "@/hooks/use-app-translation";
import type {
	WorkflowActionDisplay,
	WorkflowConditionDisplay,
	WorkflowRuleDisplay,
	WorkflowTriggerDisplay,
} from "@/services/workflow/workflow.types";
import { RULE_FORM_CONDITION_FIELDS } from "@/utils/workflow-rules/workflow-rule-form-utils";

// ─── Trigger ──────────────────────────────────────────────────────────────────

/**
 * Returns the human-readable trigger type label.
 * Uses existing create-modal i18n keys so no new translations are needed for
 * the label itself.
 */
export function formatTriggerLabel(
	type: string | null | undefined,
	t: AppTranslateFn,
): string {
	switch (type) {
		case "entity_created":
			return t(
				"admin.workflowRules.create.fields.trigger.options.entityCreated",
			);
		case "status_entered":
			return t(
				"admin.workflowRules.create.fields.trigger.options.statusEntered",
			);
		case "status_exited":
			return t(
				"admin.workflowRules.create.fields.trigger.options.statusExited",
			);
		default:
			return type ?? "—";
	}
}

/**
 * Returns the resolved status name (or raw id fallback) for triggers that
 * carry a status config value. Returns null if there's nothing to show.
 */
export function formatTriggerDetail(
	trigger: WorkflowTriggerDisplay | null | undefined,
	statusNameMap: Record<string, string>,
): string | null {
	if (!trigger) return null;
	const config = trigger.config as Record<string, unknown> | null | undefined;
	if (!config) return null;
	const rawStatusId =
		(config.StatusId as string | undefined) ??
		(config.statusId as string | undefined);
	if (!rawStatusId) return null;
	return statusNameMap[rawStatusId.toLowerCase()] ?? rawStatusId;
}

// ─── Actions ──────────────────────────────────────────────────────────────────

export interface ActionDisplayResult {
	label: string;
	detail: string | null;
}

/**
 * Returns a human-readable {label, detail} pair for a single action.
 */
export function formatActionDisplay(
	action: WorkflowActionDisplay | null | undefined,
	statusNameMap: Record<string, string>,
	t: AppTranslateFn,
	userNameMap?: Record<string, string>,
): ActionDisplayResult {
	if (!action?.type) return { label: "—", detail: null };

	const cfg = action.config as Record<string, unknown> | null | undefined;

	switch (action.type) {
		case "set_status": {
			const label = t("admin.workflowRules.create.actions.type.setStatus");
			const rawId =
				(cfg?.StatusId as string | undefined) ??
				(cfg?.statusId as string | undefined);
			const statusName = rawId
				? (statusNameMap[rawId.toLowerCase()] ?? rawId)
				: null;
			return { label, detail: statusName };
		}
		case "set_assignee": {
			const label = t("admin.workflowRules.create.actions.type.setAssignee");
			const userId =
				(cfg?.UserId as string | undefined) ??
				(cfg?.userId as string | undefined) ??
				(cfg?.Assignee as string | undefined) ??
				(cfg?.assignee as string | undefined);
			const storedName =
				(cfg?.AssigneeName as string | undefined) ??
				(cfg?.assigneeName as string | undefined);
			const detail = userId
				? (storedName ??
					userNameMap?.[userId] ??
					userNameMap?.[userId.toLowerCase()] ??
					userId)
				: null;
			return { label, detail };
		}
		case "send_email": {
			const label = t("admin.workflowRules.create.actions.type.sendEmail");
			const recipientType =
				(cfg?.RecipientType as string | undefined) ??
				(cfg?.recipientType as string | undefined);
			const emailsArray = (cfg?.Emails ?? cfg?.emails) as string[] | undefined;
			const recipientEmail =
				emailsArray?.[0] ??
				(cfg?.RecipientEmail as string | undefined) ??
				(cfg?.recipientEmail as string | undefined);
			const detail = recipientEmail ?? recipientType ?? null;
			return { label, detail };
		}
		case "create_entity": {
			const label = t("admin.workflowRules.create.actions.type.createEntity");
			const nameTemplate =
				(cfg?.NameTemplate as string | undefined) ??
				(cfg?.nameTemplate as string | undefined);
			return { label, detail: nameTemplate ?? null };
		}
		default:
			return { label: action.type, detail: null };
	}
}

// ─── Conditions ───────────────────────────────────────────────────────────────

/** Maps operator codes to human-readable labels */
export function formatOperatorLabel(
	operator: string,
	t: AppTranslateFn,
): string {
	switch (operator) {
		case "eq":
			return t("admin.workflowRules.drawer.operators.eq");
		case "ne":
			return t("admin.workflowRules.drawer.operators.ne");
		case "gt":
			return t("admin.workflowRules.drawer.operators.gt");
		case "lt":
			return t("admin.workflowRules.drawer.operators.lt");
		case "gte":
			return t("admin.workflowRules.drawer.operators.gte");
		case "lte":
			return t("admin.workflowRules.drawer.operators.lte");
		case "isnull":
			return t("admin.workflowRules.drawer.operators.isnull");
		case "isnotnull":
			return t("admin.workflowRules.drawer.operators.isnotnull");
		case "contains":
			return t("admin.workflowRules.drawer.operators.contains");
		default:
			return operator;
	}
}

const NO_VALUE_OPERATORS = new Set(["isnull", "isnotnull"]);

const UUID_REGEX =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(s: string): boolean {
	return UUID_REGEX.test(s);
}

/**
 * Converts a camelCase or PascalCase identifier to "Title Case With Spaces".
 *   "createdAt"  → "Created At"
 *   "dueDate"    → "Due Date"
 *   "myField"    → "My Field"
 */
function camelToLabel(str: string): string {
	return str
		.replace(/([A-Z])/g, " $1")
		.replace(/^(.)/, (c) => c.toUpperCase())
		.trim();
}

const DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Formats an ISO date or datetime string into a human-readable form.
 * Falls back to the original string for any other value.
 *   "2026-07-06T00:10:00" → "Jul 6, 2026 at 12:10 AM"
 *   "2026-07-06"          → "Jul 6, 2026"
 */
function formatConditionValue(raw: string): string {
	if (DATE_TIME_RE.test(raw)) {
		try {
			return new Intl.DateTimeFormat("en-US", {
				month: "short",
				day: "numeric",
				year: "numeric",
				hour: "numeric",
				minute: "2-digit",
				hour12: true,
			}).format(new Date(raw));
		} catch {
			return raw;
		}
	}
	if (DATE_ONLY_RE.test(raw)) {
		try {
			const [year, month, day] = raw.split("-").map(Number);
			// Construct as local date to avoid UTC midnight → previous-day shift.
			return new Intl.DateTimeFormat("en-US", {
				month: "short",
				day: "numeric",
				year: "numeric",
			}).format(new Date(year, month - 1, day));
		} catch {
			return raw;
		}
	}
	return raw;
}

export interface ConditionDisplayParts {
	fieldLabel: string;
	fieldId: string | null;
	operatorLabel: string;
	/** Null for operators that do not take a compare value (isnull / isnotnull). */
	valueLabel: string | null;
}

/**
 * Builds id → label map from built-in condition fields plus custom form fields.
 */
export function buildFieldLabelMap(
	customFields: { id: string; label: string }[],
): Record<string, string> {
	const map: Record<string, string> = {};
	for (const field of RULE_FORM_CONDITION_FIELDS) {
		map[field.id] = field.label;
	}
	for (const field of customFields) {
		if (field.id && field.label) map[field.id] = field.label;
	}
	return map;
}

/**
 * Resolves a condition into readable field / operator / value parts for UI.
 */
export function getConditionDisplayParts(
	condition: WorkflowConditionDisplay | null | undefined,
	fieldLabelMap: Record<string, string>,
	t: AppTranslateFn,
): ConditionDisplayParts {
	if (!condition) {
		return {
			fieldLabel: "—",
			fieldId: null,
			operatorLabel: "",
			valueLabel: null,
		};
	}

	const columnId = condition.columnDefinitionId ?? null;
	const entityProp = condition.entityPropertyName ?? null;
	const fieldId = columnId ?? entityProp;

	let fieldLabel: string;
	if (columnId && fieldLabelMap[columnId]) {
		fieldLabel = fieldLabelMap[columnId];
	} else if (entityProp && fieldLabelMap[entityProp]) {
		fieldLabel = fieldLabelMap[entityProp];
	} else if (entityProp && !isUuid(entityProp)) {
		// Edit path may store a human label (Title Case / spaced). Built-in
		// property names are camelCase and still need camelToLabel.
		const looksHumanLabel =
			entityProp.includes(" ") || /^[A-Z]/.test(entityProp);
		fieldLabel = looksHumanLabel ? entityProp : camelToLabel(entityProp);
	} else {
		fieldLabel = camelToLabel(fieldId ?? "?");
	}

	const operatorLabel = formatOperatorLabel(condition.operator ?? "", t);
	const noValue = NO_VALUE_OPERATORS.has(condition.operator ?? "");
	if (noValue) {
		return { fieldLabel, fieldId, operatorLabel, valueLabel: null };
	}

	const rawValue =
		condition.compareToStringValue ??
		condition.compareToNumericValue?.toString() ??
		"?";
	return {
		fieldLabel,
		fieldId,
		operatorLabel,
		valueLabel: formatConditionValue(rawValue),
	};
}

/**
 * Joins condition display parts into a single-line label string.
 */
export function formatConditionPartsLabel(
	parts: ConditionDisplayParts,
): string {
	if (!parts.operatorLabel && parts.fieldLabel === "—") return "—";
	if (parts.valueLabel === null) {
		return `${parts.fieldLabel} ${parts.operatorLabel}`.trim();
	}
	return `${parts.fieldLabel} ${parts.operatorLabel} '${parts.valueLabel}'`;
}

/**
 * Returns a single-line "{field} {operator} {value}" string for a condition.
 */
export function formatConditionLabel(
	condition: WorkflowConditionDisplay | null | undefined,
	t: AppTranslateFn,
	fieldLabelMap: Record<string, string> = {},
): string {
	return formatConditionPartsLabel(
		getConditionDisplayParts(condition, fieldLabelMap, t),
	);
}

// ─── Rule overview (monospace summary block) ──────────────────────────────────

export interface RuleOverviewParts {
	whenLine: string;
	conditions: ConditionDisplayParts[];
	thenLine: string | null;
}

/**
 * Builds structured overview parts so the UI can tooltip condition field names.
 */
export function formatRuleOverviewParts(
	rule: WorkflowRuleDisplay,
	statusNameMap: Record<string, string>,
	t: AppTranslateFn,
	fieldLabelMap: Record<string, string> = {},
	userNameMap?: Record<string, string>,
): RuleOverviewParts | null {
	const version = rule.latestVersion ?? rule.activeVersion;
	if (!version) return null;

	const triggerType = version.trigger?.type;
	const triggerLbl = formatTriggerLabel(triggerType, t);
	const triggerDetail = formatTriggerDetail(version.trigger, statusNameMap);
	const whenLine = triggerDetail
		? `${t("admin.workflowRules.drawer.summary.when")}: ${triggerLbl} → '${triggerDetail}'`
		: `${t("admin.workflowRules.drawer.summary.when")}: ${triggerLbl}`;

	const conditions = (version.condition?.conditions ?? []).map((c) =>
		getConditionDisplayParts(c, fieldLabelMap, t),
	);

	const actions = version.actions ?? [];
	const actionLabels = actions.map((a) => {
		const { label, detail } = formatActionDisplay(
			a,
			statusNameMap,
			t,
			userNameMap,
		);
		return detail ? `${label} → '${detail}'` : label;
	});
	const thenLine =
		actions.length > 0
			? `${t("admin.workflowRules.drawer.summary.then")}: ${actionLabels.join(", ")}`
			: null;

	return { whenLine, conditions, thenLine };
}

/**
 * Builds the 2-3 line plain-text summary rendered in the monospace block:
 *   When: <trigger label> [→ '<status name>']
 *   If: <cond1> AND <cond2> ...   (omitted when no conditions)
 *   Then: <action1>, <action2>, ...
 */
export function formatRuleOverview(
	rule: WorkflowRuleDisplay,
	statusNameMap: Record<string, string>,
	t: AppTranslateFn,
	userNameMap?: Record<string, string>,
	fieldLabelMap: Record<string, string> = {},
): string {
	const parts = formatRuleOverviewParts(
		rule,
		statusNameMap,
		t,
		fieldLabelMap,
		userNameMap,
	);
	if (!parts) return "";

	const ifLine =
		parts.conditions.length > 0
			? `${t("admin.workflowRules.drawer.summary.if")}: ${parts.conditions
					.map((c) => formatConditionPartsLabel(c))
					.join(` ${t("admin.workflowRules.drawer.summary.and")} `)}`
			: null;

	return [parts.whenLine, ifLine, parts.thenLine].filter(Boolean).join("\n");
}
